import {computed, inject, Injectable, signal} from '@angular/core';
import {CompanyResponse} from '@frontend/shared-api';
import {ModuleFunction} from '@frontend/directus';
import {OfficeViewModel} from '../project-wizard-office-step/office.models';
import {ProjectWizardOfficeDataService} from './project-wizard-office-data.service';

@Injectable({
  providedIn: 'root',
})
export class CompanyOfficeFunctionSelectionService {
  private readonly officeDataService = inject(ProjectWizardOfficeDataService);
  private readonly companiesData = signal<CompanyResponse[]>([]);
  readonly selectedCompanyIds = signal<Set<number>>(new Set());
  readonly selectedOfficeIds = signal<Set<number>>(new Set());
  readonly officeFunctionsMap = signal<Record<number, ModuleFunction[]>>({});

  readonly selectedCompanies = computed(() => {
    const ids = this.selectedCompanyIds();
    return this.companiesData().filter((company) => ids.has(company.id));
  });

  setCompaniesData(companies: CompanyResponse[]) {
    this.companiesData.set(companies);
  }

  toggleCompany(id: number) {
    const currentCompanies = this.selectedCompanyIds();
    const newSet = new Set(currentCompanies);

    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }

    this.selectedCompanyIds.set(newSet);
  }

  toggleOffice(id: number) {
    const currentOffices = this.selectedOfficeIds();
    const newSet = new Set(currentOffices);

    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }

    this.selectedOfficeIds.set(newSet);
  }

  getSelectedOffices(): Set<number> {
    return this.selectedOfficeIds();
  }

  setOfficeFunctions(officeId: number, functions: ModuleFunction[]) {
    this.officeFunctionsMap.update((currentMap) => {
      const functionsCopy = functions.map((f) => ({...f}));
      return {...currentMap, [officeId]: functionsCopy};
    });
  }

  getOfficeFunctions(officeId: number): ModuleFunction[] {
    return this.officeFunctionsMap()[officeId] || [];
  }

  getAllOfficeFunctions(): Record<number, ModuleFunction[]> {
    return this.officeFunctionsMap();
  }

  clearOfficeFunctions(officeId: number) {
    this.officeFunctionsMap.update((currentMap) => {
      const newMap = {...currentMap};
      delete newMap[officeId];
      return newMap;
    });
  }

  getOfficeViewModel(officeId: number): OfficeViewModel | undefined {
    const allOffices = this.officeDataService.flattenCompanyData(this.companiesData());
    return allOffices.find((o) => o.id === officeId);
  }
}
